import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { LayerGroup, GeoJSON } from "react-leaflet";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { setEnvironmentalDataSelectedSegment } from "redux/actions";

const mapZoomSelector = (state) => state.sessionFilters.mapBounds.zoom;
const mapEnvironmentDataSelector = (state) => state.environmentData.mapData;
const mapEnvironmentDataFiltersSelector = (state) => state.environmentData.filters;
const mapEnvironmentDataSelectedSegmentSelector = (state) => state.environmentData.selectedSegment;
const environmentDataLoadedTypesSelector = (state) => state.environmentData.dataLoadedTypes;

// below values might have to be changed if more data added to the database (also will have to be updated in SatelliteDataTab.js)
const DATA_SUB_TYPES_MAP = {
    vegetation_encroachment: [
        "data_type",
        "sub_data_type",
        "route_id",
        "full_data.vegetation_density_percentage",
        "full_data.display_fields.Vegetation Encroachment Status",
    ],
    vegetation_density: ["data_type", "sub_data_type", "route_id", "full_data.display_fields.Vegetation Density Level"],
    pluvial_flood_risk: ["data_type", "sub_data_type", "route_id", "full_data.display_fields.Pluvial Flood Risk"],
    fluvial_flood_risk: ["data_type", "sub_data_type", "route_id", "full_data.display_fields.Fluvial Flood Risk"],
    land_movement_average_velocity: [
        "data_type",
        "sub_data_type",
        "route_id",
        "full_data.number_of_points",
        "full_data.average_velocity",
        "full_data.display_fields.Land Movement Average Velocity",
    ],
    land_movement_velocity_rate_of_change: ["data_type", "sub_data_type", "route_id", "full_data.number_of_points", "full_data.velocity_rate_of_change"],
};

const EnvironmentalPolygons = memo(({ minZoom }) => {
    const dispatch = useDispatch();

    const mapZoom = useSelector(mapZoomSelector);
    const mapEnvironmentData = useSelector(mapEnvironmentDataSelector);
    const mapEnvironmentDataSelectedSegment = useSelector(mapEnvironmentDataSelectedSegmentSelector);
    const mapEnvironmentDataFilters = useSelector(mapEnvironmentDataFiltersSelector);
    const environmentDataLoadedTypes = useSelector(environmentDataLoadedTypesSelector);

    const [selectedSection, setSelectedSection] = useState(null);

    useEffect(() => {
        const currentRouteID = _.get(mapEnvironmentDataSelectedSegment, "route_id", null);
        const selectedRouteID = _.get(selectedSection, "route_id", null);

        if (currentRouteID !== selectedRouteID) {
            setSelectedSection(mapEnvironmentDataSelectedSegment);
        } else if (!currentRouteID) {
            setSelectedSection(null);
        }
    }, [mapEnvironmentDataSelectedSegment, selectedSection]);

    const selectedSectionRouteID = useMemo(() => {
        return _.get(selectedSection, "route_id", null);
    }, [selectedSection]);

    const selectedSectionID = useMemo(() => {
        return _.get(mapEnvironmentDataSelectedSegment, "id", null);
    }, [mapEnvironmentDataSelectedSegment]);

    const filterType = useMemo(() => {
        return _.get(mapEnvironmentDataFilters, "type");
    }, [mapEnvironmentDataFilters]);

    const filterSubType = useMemo(() => {
        return _.get(mapEnvironmentDataFilters, "subType");
    }, [mapEnvironmentDataFilters]);

    const isCurrentDataTypeLoaded = useMemo(() => {
        const isLoaded = _.get(environmentDataLoadedTypes, [filterType, filterSubType]);
        return isLoaded;
    }, [environmentDataLoadedTypes, filterSubType, filterType]);

    const selectedEnvironmentDataMapData = useMemo(() => {
        let _data = [];
        if (isCurrentDataTypeLoaded) {
            _data = _.get(mapEnvironmentData, [filterType, filterSubType]);
        }

        return _data;
    }, [mapEnvironmentData, filterSubType, filterType, isCurrentDataTypeLoaded]);

    // this is where we do colouring and default values are predefined here too as a list [green, orange, red]
    const setEnvironmentDataFeatureStyle = (feature, filterType, filterSubType, mapZoom, selectedSectionID = null) => {
        let weight = mapZoom < 11 ? 2 : 1; // Border width
        let opacity = mapZoom <= 12 ? 1 : 0.7; // Border opacity
        let fillOpacity = mapZoom <= 12 ? 1 : 0.7; // Fill opacity

        const fullData = _.get(feature, ["properties", "full_data"], null);
        const data = _.get(feature, ["properties", "data"], null);
        const id = _.get(feature, ["properties", "id"], null);

        // this is mainly for colouring selected ELR segments... to highligh current sections
        if (selectedSectionID && selectedSectionID === id) {
            return {
                color: "#673ab7",
                weight: 3,
                opacity: 1,
                fillOpacity,
            };
        }

        let valueToCompare = null;
        let valueThresholds = [];
        let colour = "#adc200";

        if (filterSubType === "vegetation_density") {
            valueToCompare = data;
            if (valueToCompare === "high") {
                colour = "#ed4747"; // Red
            } else if (valueToCompare === "medium") {
                colour = "#f99244"; // '#f99244' Amber
            } else {
                colour = "#54c333"; // '#54c333' Green
            }
            return {
                color: colour,
                weight,
                opacity,
                fillOpacity,
            };
        } else if (filterSubType === "land_movement_average_velocity") {
            valueToCompare = Math.abs(_.get(fullData, ["average_velocity"], null));
            valueThresholds = [1, 2];
        } else if (filterSubType === "land_movement_velocity_rate_of_change") {
            valueToCompare = _.get(fullData, ["velocity_rate_of_change"], null);
            valueThresholds = [0.5, 1];
        } else if (filterSubType === "vegetation_encroachment") {
            valueToCompare = data;
            if (valueToCompare === "encroaching") {
                colour = "#ed4747"; // Red
            } else if (valueToCompare === "not_encroaching") {
                colour = "#54c333";
            }
            return {
                color: colour,
                weight,
                opacity,
                fillOpacity,
            };
        } else if (filterType === "flood_risk") {
            valueToCompare = _.get(fullData, ["frequency"], null);
            if (!valueToCompare) {
                colour = "#54c333"; // '#54c333' Green
            } else if (valueToCompare <= 10) {
                colour = "#ed4747"; // Red
            } else if (10 < valueToCompare && valueToCompare <= 100) {
                colour = "#f99244"; // '#f99244' Amber
            } else {
                colour = "#54c333"; // '#54c333' Green
            }
            return {
                color: colour,
                weight,
                opacity,
                fillOpacity,
            };
        }

        if (valueToCompare) {
            if (valueToCompare <= valueThresholds[0]) {
                colour = "#54c333"; // '#54c333' Green
            } else if (valueToCompare <= valueThresholds[1]) {
                colour = "#f99244"; // '#f99244' Amber
            } else {
                colour = "#ed4747"; // Red
            }
        }

        return {
            color: colour,
            weight,
            opacity,
            fillOpacity,
        };
    };

    const selectedRouteIdGeoJSON = useMemo(() => {
        const selectedSectionRouteID = _.get(selectedSection, "route_id");
        const selectedElrData = _.filter(selectedEnvironmentDataMapData, { properties: { route_id: selectedSectionRouteID } }, []);

        return selectedElrData;
    }, [selectedSection, selectedEnvironmentDataMapData]);

    const onEachEnvironmentDataFeature = useCallback(
        (feature, layer) => {
            const properties = feature.properties;
            const subTypes = _.get(DATA_SUB_TYPES_MAP, filterSubType);
            const sectionID = _.get(properties, "id");

            let tooltipContent = `<div class="SatelliteDataTooltipMainDiv">`;

            _.map(subTypes, (type) => {
                const keysList = _.split(type, ".");
                const rowLabel = keysList.at(-1);
                let value = _.get(properties, keysList, null);

                if (keysList.at(-1) === "vegetation_density_percentage") {
                    value = `${_.floor(value, 2)}%`;
                }
                if (["data_type", "sub_data_type"].includes(keysList.at(-1))) {
                    value = _.startCase(value);
                }

                if (value) {
                    tooltipContent += `
                    <div class="SatelliteDataTabInfoContainerItem">
                        <div class="SatelliteDataTabInfoContainerItemLabel">${_.startCase(rowLabel)}:</div>
                        <div class="SatelliteDataTabInfoContainerItemValue">${value}</div>
                    </div>`;
                }
            });

            tooltipContent += "</div>";

            if (!selectedSectionID) {
                layer.bindTooltip(tooltipContent, {
                    permanent: false,
                    key: `${filterSubType}-${sectionID}`,
                    direction: "top",
                    offset: [0, -10],
                    className: "SatelliteDataTooltip",
                    delay: 0,
                });
            }

            layer.on({
                click: () => {
                    dispatch(setEnvironmentalDataSelectedSegment(properties));
                },
            });
        },
        [dispatch, filterSubType, selectedSectionID],
    );

    return (
        <LayerGroup>
            {mapZoom >= minZoom && !selectedRouteIdGeoJSON.length && isCurrentDataTypeLoaded ? (
                <GeoJSON
                    data={selectedEnvironmentDataMapData}
                    key={`${filterType}-${filterSubType}`}
                    style={(feature) => setEnvironmentDataFeatureStyle(feature, filterType, filterSubType, mapZoom)}
                    onEachFeature={onEachEnvironmentDataFeature}
                />
            ) : null}

            <GeoJSON
                key={`selected-lines-${selectedSectionRouteID}`}
                data={selectedRouteIdGeoJSON}
                style={(feature) => setEnvironmentDataFeatureStyle(feature, filterType, filterSubType, mapZoom, selectedSectionID)}
                onEachFeature={onEachEnvironmentDataFeature}
            />
        </LayerGroup>
    );
});

export default EnvironmentalPolygons;
